import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, UserMinus, Calendar, User, Check, Loader } from 'lucide-react';
import participantService from '../../services/participantService';
import { toast } from 'react-hot-toast';

const ParticipantDeactivation = () => {
  const { studyID, siteID, participantID } = useParams();
  const navigate = useNavigate();
  const [deactivationDate, setDeactivationDate] = useState('');
  const [confirmDeactivation, setConfirmDeactivation] = useState(false);
  const [participant, setParticipant] = useState(null);
  const [displayParticipantId, setDisplayParticipantId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParticipant = async () => {
      try {
        const data = await participantService.getParticipant(participantID);
        console.log('Full participant data:', data);
        setParticipant(data);
        
        // Extract human-readable ID (try multiple possible locations in the API response)
        const humanReadableId = data?.participant_id || 
                               data?.id || 
                               data?.participant?.participant_id || 
                               '';
        
        // If we still don't have a human-readable ID, use the breadcrumb service
        if (!humanReadableId || humanReadableId === participantID) {
          // We'll fetch from the breadcrumb service in another function
          fetchHumanReadableIdFromBreadcrumbs();
        } else {
          setDisplayParticipantId(humanReadableId);
        }
      } catch (error) {
        console.error('Error fetching participant:', error);
        toast.error('Failed to fetch participant details.');
        // Still try the breadcrumb service as a fallback
        fetchHumanReadableIdFromBreadcrumbs();
      } finally {
        setLoading(false);
      }
    };
    
    const fetchHumanReadableIdFromBreadcrumbs = async () => {
      try {
        // Import dynamically to avoid circular dependencies
        const BreadcrumbService = (await import('../../services/breadcrumbService')).default;
        const breadcrumbData = await BreadcrumbService.getBreadcrumbData();
        
        if (breadcrumbData && breadcrumbData.participants && breadcrumbData.participants[participantID]) {
          setDisplayParticipantId(breadcrumbData.participants[participantID]);
        }
      } catch (err) {
        console.error('Error fetching from breadcrumb service:', err);
      }
    };
    
    fetchParticipant();
  }, [participantID]);

  const handleRedirect = () => {
    const redirectSiteID = siteID || (participant?.site?.id);
    if (redirectSiteID) {
      navigate(`/studies/${studyID}/sites/${redirectSiteID}`);
    } else {
      console.warn('Site ID not found, redirecting to study page');
      navigate(`/studies/${studyID}`);
    }
  };

  const isNotLaterThanToday = (dateStr) => {
    let selectedDate = new Date(dateStr + " 00:00:01");
    let today = new Date();
    today.setHours(23, 59, 59);
    return selectedDate <= today;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmDeactivation) {
      toast.error('Please confirm that you want to deactivate this participant.');
      return;
    }
    if (!deactivationDate) {
      toast.error('Please select a deactivation date.');
      return;
    }
    if (!isNotLaterThanToday(deactivationDate)) {
      toast.error('The selected date must be today or in the past.');
      return;
    }
	
    try {
      await participantService.deactivateParticipant(participantID, deactivationDate);
      toast.success('Participant deactivated successfully');
      handleRedirect();
    } catch (error) {
      console.error('Error deactivating participant:', error);
      toast.error('Failed to deactivate participant. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white dark:bg-gray-800 shadow-xl dark:shadow-gray-900/20 rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-primary-700">
              <h2 className="text-2xl font-semibold flex items-center text-white">
                <UserMinus className="w-6 h-6 mr-2" />
                Participant Deactivation
              </h2>
            </div>
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <p className="text-gray-700 dark:text-gray-200 mb-4">
                  Do NOT complete this form if the participant is to remain active in the portal and/or appear on
                  eCOA devices. Only complete this form if the participant has ceased all activities, and should be
                  deactivated from the portal and/or device.
                </p>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 mr-3" />
                    <p className="text-sm text-yellow-700">
                      PLEASE CONTACT THE HELPDESK IF A PARTICIPANT HAS BEEN DEACTIVATED IN ERROR.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2" htmlFor="participant-id">
                      Participant ID
                    </label>
                    <div className="relative">
                      <input
                        id="participant-id"
                        type="text"
                        value={displayParticipantId || participantID}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2" htmlFor="deactivation-date">
                      Date participant was no longer active
                    </label>
                    <div className="relative">
                      <input
                        id="deactivation-date"
                        type="date"
                        value={deactivationDate}
                        onChange={(e) => setDeactivationDate(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        style={{ colorScheme: 'light' }}
                      />
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" size={20} />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={confirmDeactivation}
                          onChange={(e) => setConfirmDeactivation(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded-sm transition-colors duration-200 ease-in-out ${confirmDeactivation ? 'bg-primary-500 border-primary-500' : 'border-gray-300 dark:border-gray-600'}`}>
                          <Check className={`w-4 h-4 text-white transition-opacity duration-200 ease-in-out ${confirmDeactivation ? 'opacity-100' : 'opacity-0'}`} />
                        </div>
                      </div>
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                        YES, I want to deactivate this participant.
                      </span>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleRedirect}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                    >
                      Cancel
                    </button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150 ease-in-out"
                    >
                      <UserMinus className="w-5 h-5 mr-2" />
                      Deactivate
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ParticipantDeactivation;